﻿import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Header from './componentes/js/cabecalho';
import logo from './componentes/images/logo_fundo_branco.png';
import Login from './componentes/js/login';
import CadastroMaster from './componentes/js/cadastroMaster';
import AtivarConta from './componentes/js/ativarConta';
import EsqueciSenha from './componentes/js/esqueciSenha';
import RedefinirSenha from './componentes/js/redefinirSenha';
import TrocarSenha from './componentes/js/trocarSenha';
import SeletorClienteModal from './componentes/js/seletorClienteModal';
import GestaoUsuarios from './componentes/js/gestaoUsuarios';
import CriarUsuarioSecundario from './componentes/js/criarUsuarioSecundario';
import EditarUsuarioSecundario from './componentes/js/editarUsuarioSecundario';
import Dashboard from './componentes/js/dashboard';
import InicioPortal from './componentes/js/inicioPortal';
import Resumo from './componentes/js/resumo';
import Boletos from './componentes/js/boletos';
import Danfe from './componentes/js/danfe';
import DadosCadastrais from './componentes/js/dadosCadastrais';
import RegistroSAC from './componentes/js/registroSAC';
import Financeiro from './componentes/js/financeiro';
import ConsultaSAC from './componentes/js/consultaSAC';
import RegistroFeedback from './componentes/js/registroFeedback'; // Novo Import
import ConsultaFeedback from './componentes/js/consultaFeedback'; // Novo Import
import ClienteService from './modules/services/ClienteService';
import AuthService from './modules/services/AuthService';
import MultiAcessoService from './modules/services/MultiAcessoService';
import UsuarioSecundarioService from './modules/services/UsuarioSecundarioService';
import { temModulo } from './utils/acesso';
import Footer from './componentes/js/rodape';
import ChatWidget from './componentes/js/chatWidget';
import './App.css';

// Telas de auto-cadastro/recuperação de senha: acessíveis sempre, sem sessão nenhuma —
// ficam fora de todo o gate de login/token abaixo.
const ROTAS_PUBLICAS = {
    '/cadastro': CadastroMaster,
    '/ativar-conta': AtivarConta,
    '/esqueci-senha': EsqueciSenha,
    '/redefinir-senha': RedefinirSenha,
};

// Guarda de rota: hoje o menu já esconde os itens sem permissão, mas isso não impede o acesso
// direto pela URL nem por atalhos internos (ex.: cards do dashboard que navegam direto para
// /boletos ou /danfe). Sem isso, um usuário sem o módulo consegue abrir a tela mesmo sem o item
// aparecer no menu. Redireciona para a Home quando a tela pedida não é permitida.
function RotaProtegida({ permitido, children }) {
    if (!permitido) {
        return <Navigate to="/" replace />;
    }
    return children;
}

function App() {
    const url = new URLSearchParams(window.location.search);
    const token = url.get("token");
    const acessoAdminToken = url.get("acessoAdmin");
    const rotaPublica = ROTAS_PUBLICAS[window.location.pathname];

    const [cliente, setCliente] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [updateKey, setUpdateKey] = useState(0);
    const [mostrarLogin, setMostrarLogin] = useState(false);
    const [portalSession, setPortalSession] = useState(null);
    const [mostrarSeletorInicial, setMostrarSeletorInicial] = useState(false);
    // false | 'primeiro-acesso' | 'expirada' — motivo da troca obrigatória, usado pra adaptar o
    // texto da tela (TrocarSenha.js).
    const [motivoTrocarSenha, setMotivoTrocarSenha] = useState(false);

    const atualizarDadosCliente = async () => {
        try {
            console.log('🔄 Iniciando atualização dos dados do cliente...');
            const codigoCliente = sessionStorage.getItem('codigoCliente');
            if (!codigoCliente) {
                console.error('Código do cliente não encontrado na sessão');
                return;
            }
            console.log('📞 Consultando API para código do cliente:', codigoCliente);
            const fetchedCliente = await ClienteService.getDadosCliente(codigoCliente);
            console.log('✅ Dados do cliente atualizados:', fetchedCliente);
            setCliente(fetchedCliente);
            setUpdateKey(prev => prev + 1); // Força re-renderização
        } catch (err) {
            console.error('❌ Erro ao atualizar dados do cliente:', err);
        }
    };

    // Recarrega a lista de usuários secundários do servidor e já deixa pronta na sessão (estado +
    // sessionStorage) — chamado depois de criar OU editar um usuário, pra GestaoUsuarios abrir
    // com a lista já completa e atualizada, sem depender da "foto" tirada no login (que pode
    // estar desatualizada) nem de juntar/remendar manualmente no lado do cliente.
    const recarregarUsuariosSecundarios = async () => {
        if (!portalSession?.usuario?.cdUsuario) {
            return;
        }
        try {
            const lista = await UsuarioSecundarioService.listarPorMaster(portalSession.usuario.cdUsuario);
            setPortalSession((atual) => {
                if (!atual) {
                    return atual;
                }
                const atualizado = { ...atual, usuariosSecundarios: lista || [] };
                sessionStorage.setItem('portalSession', JSON.stringify(atualizado));
                return atualizado;
            });
        } catch (error) {
            // Se falhar, GestaoUsuarios ainda faz seu próprio refetch ao montar — não é bloqueante.
            console.error('Erro ao recarregar usuários secundários após cadastro:', error);
        }
    };

    useEffect(() => {
        if (rotaPublica) {
            setLoading(false);
            return;
        }

        const inicializacao = async () => {
            try {
                let codigoCliente;

                if (acessoAdminToken) {
                    // Link "acessar como" (gerado por um sistema administrativo, ex.: GestaoGamPraVoce):
                    // consome o token de uso único e monta a sessão exatamente como um login normal.
                    sessionStorage.clear();
                    window.history.replaceState(null, '', window.location.pathname);

                    let conteudo;
                    try {
                        conteudo = await MultiAcessoService.validarAcessoAdministrativo(acessoAdminToken);
                    } catch (erroAcesso) {
                        setError(erroAcesso.message || "Link de acesso inválido ou expirado.");
                        return;
                    }

                    await handleLoginSuccess(conteudo);
                    return;
                } else if (token) {
                    sessionStorage.clear();
                    codigoCliente = await AuthService.hmac(token);
                    if (!codigoCliente) {
                        setError("Usuário inválido, por favor tente novamente.");
                        return;
                    }
                    sessionStorage.setItem('codigoCliente', codigoCliente);
                } else {
                    const portalSessionSalva = sessionStorage.getItem('portalSession');
                    if (portalSessionSalva) {
                        const sessao = JSON.parse(portalSessionSalva);
                        AuthService.setPortalToken(sessao.token);
                        setPortalSession(sessao);
                    }

                    codigoCliente = sessionStorage.getItem('codigoCliente');
                    if (!codigoCliente) {
                        setMostrarLogin(true);
                        return;
                    }
                }
                const fetchedCliente = await ClienteService.getDadosCliente(codigoCliente);
                setCliente(fetchedCliente);
            } catch (err) {
                setError("Erro ao carregar os dados do cliente, por favor entre em contato com o SAC.");
            } finally {
                setLoading(false);
            }
        };

        inicializacao();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleLoginSuccess = async (conteudo) => {
        const sessao = {
            usuario: {
                cdUsuario: conteudo.cdUsuario,
                dsLogin: conteudo.dsLogin,
                nmUsuario: conteudo.nmUsuario,
                dsEmail: conteudo.dsEmail,
            },
            clientesPerfis: conteudo.clientesPerfis || [],
            cdUsuarioMaster: conteudo.cdUsuarioMaster,
            token: conteudo.token,
            // Só vem preenchido para o usuário master (RN009) — carregado já no login para a tela
            // "Usuários e Permissões" abrir instantânea, sem esperar uma consulta nova.
            usuariosSecundarios: conteudo.usuariosSecundarios || null,
        };

        if (sessao.clientesPerfis.length === 0) {
            setError("Este usuário não possui clientes vinculados. Entre em contato com o SAC.");
            setMostrarLogin(false);
            return;
        }

        AuthService.setPortalToken(sessao.token);
        sessionStorage.setItem('portalSession', JSON.stringify(sessao));
        setPortalSession(sessao);
        setMostrarLogin(false);

        // RN007: primeiro acesso exige troca de senha antes de prosseguir para a
        // seleção de cliente/dashboard. Senha expirada (90 dias) tem a mesma trava, só que o
        // motivo exibido na tela é outro — o backend libera o login (emite token) nos dois
        // casos, mas só para trocar a senha.
        if (conteudo.primeiroAcesso) {
            setMotivoTrocarSenha('primeiro-acesso');
            return;
        }
        if (conteudo.senhaExpirada) {
            setMotivoTrocarSenha('expirada');
            return;
        }

        await prosseguirAposLogin(sessao);
    };

    const handleLogout = () => {
        sessionStorage.removeItem("portalSession");
        sessionStorage.removeItem("codigoCliente");

        AuthService.clearPortalToken();

        setPortalSession(null);
        setCliente("");
        setMostrarSeletorInicial(false);
        setMotivoTrocarSenha(false);
        setMostrarLogin(true);
};

    const prosseguirAposLogin = async (sessao) => {
        // Regra "apenas um cliente": pula a seleção e já entra direto no sistema.
        if (sessao.clientesPerfis.length === 1) {
            await selecionarClienteEEntrar(sessao.clientesPerfis[0].cdCliente, true);
        } else {
            setMostrarSeletorInicial(true);
        }
    };

    const handleSenhaTrocada = async () => {
        setMotivoTrocarSenha(false);
        await prosseguirAposLogin(portalSession);
    };

    const selecionarClienteEEntrar = async (cdCliente, forcarInicio = false) => {
        setLoading(true);
        try {
            sessionStorage.setItem('codigoCliente', cdCliente);
            const fetchedCliente = await ClienteService.getDadosCliente(cdCliente);
            setCliente(fetchedCliente);

            // Log de acesso (multi-acesso): forcarInicio=true é a seleção logo após o login
            // (evento de ACESSO), false é uma troca de cliente feita depois, pelo cabeçalho
            // (evento de TROCA_CLIENTE). Só se aplica à sessão multi-acesso — o fluxo de token
            // na URL já registra o próprio acesso no momento da decodificação do hash.
            // Usa AuthService.hasPortalToken() em vez do estado `portalSession`: na primeira
            // chamada pós-login (handleLoginSuccess -> prosseguirAposLogin), o token do portal
            // já foi setado mas o estado React ainda não foi re-renderizado.
            if (AuthService.hasPortalToken()) {
                MultiAcessoService.selecionarCliente(cdCliente, forcarInicio);
            }

            // Pós-login, a rota deve ser sempre a Home — sem isso, se o navegador ainda tiver na
            // URL a tela em que o usuário estava antes (aba antiga, sessão expirada, etc.), o
            // <Router> monta já direto naquela rota em vez de cair na Início.
            if (forcarInicio && window.location.pathname !== '/') {
                window.history.replaceState(null, '', '/');
            }
        } catch (err) {
            setError("Erro ao carregar os dados do cliente, por favor entre em contato com o SAC.");
        } finally {
            setLoading(false);
        }
    };

    const handleSelecionarClienteInicial = async (cdCliente) => {
        setMostrarSeletorInicial(false);
        await selecionarClienteEEntrar(cdCliente, true);
    };

    const handleTrocarCliente = async (cdCliente) => {
        await selecionarClienteEEntrar(cdCliente);
    };

    if (rotaPublica) {
        const RotaPublica = rotaPublica;
        return <RotaPublica />;
    }

    if (loading) {
        return (
            <div className="loading-container">
                <img src={logo} alt="GAM" className="loading-logo" />
                <div className="spinner"></div>
                <p>Carregando Portal GAM pra você...</p>
            </div>
        );
    }

    if (mostrarLogin) {
        return <Login onSuccess={handleLoginSuccess} />;
    }

    if (motivoTrocarSenha) {
        return <TrocarSenha onSucesso={handleSenhaTrocada} motivo={motivoTrocarSenha} />;
    }

    if (mostrarSeletorInicial) {
        return (
            <SeletorClienteModal
                clientesPerfis={portalSession.clientesPerfis}
                cdClienteAtivo={null}
                onSelecionar={handleSelecionarClienteInicial}
            />
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <div className="error-icon">X</div>
                <p>{error}</p>
            </div>
        );
    }

    const souMaster = !!portalSession && (portalSession.cdUsuarioMaster === null || portalSession.cdUsuarioMaster === undefined);

    // RN014: módulos liberados para o cliente ativo na sessão de portal. undefined no fluxo antigo
    // (sem portalSession) — o cabeçalho interpreta undefined como "sem restrição, mostrar tudo".
    const clientePerfilAtivo = portalSession?.clientesPerfis?.find(
        (item) => String(item.cdCliente) === String(cliente.cdCliente)
    );
    const modulosAtivos = clientePerfilAtivo?.modulos;

    const podeFinanceiro = temModulo(modulosAtivos, "Financeiro");
    const podeSAC = temModulo(modulosAtivos, "SAC");
    const podeDadosCadastrais = temModulo(modulosAtivos, "Dados Cadastrais");

    return (
        <Router>
            <div className="App">
                <Header
                    cliente={cliente}
                    clientesPerfis={portalSession?.clientesPerfis}
                    onTrocarCliente={handleTrocarCliente}
                    onLogout={handleLogout}
                    souMaster={souMaster}
                    modulosAtivos={modulosAtivos}
                    ehMultiAcesso={!!portalSession}
                />
                <Routes>
                    <Route path="/" element={
                        portalSession
                            ? <InicioPortal cliente={cliente} modulosAtivos={modulosAtivos} souMaster={souMaster} />
                            : <Dashboard cliente={cliente} modulosAtivos={modulosAtivos} />
                    } />
                    <Route path="/dados-cadastrais" element={
                        <RotaProtegida permitido={podeDadosCadastrais}>
                            <DadosCadastrais key={updateKey} cliente={cliente} onClienteUpdate={atualizarDadosCliente} />
                        </RotaProtegida>
                    } />
                    <Route path="/resumo" element={
                        <RotaProtegida permitido={podeFinanceiro}><Resumo cliente={cliente} modulosAtivos={modulosAtivos} /></RotaProtegida>
                    } />
                    <Route path="/boletos" element={
                        <RotaProtegida permitido={podeFinanceiro}><Boletos cliente={cliente} /></RotaProtegida>
                    } />
                    <Route path="/danfe" element={
                        <RotaProtegida permitido={podeFinanceiro}><Danfe cliente={cliente} /></RotaProtegida>
                    } />
                    {/* NOVAS ROTAS DE REGISTRO E CONSULTA SAC */}
                    <Route path="/registro-sac" element={
                        <RotaProtegida permitido={podeSAC}><RegistroSAC cliente={cliente} /></RotaProtegida>
                    } />
                    <Route path="/consulta-sac" element={
                        <RotaProtegida permitido={podeSAC}><ConsultaSAC cliente={cliente} /></RotaProtegida>
                    } />
                    {/* NOVAS ROTAS DE FEEDBACK */}
                    <Route path="/registro-feedback" element={
                        <RotaProtegida permitido={podeSAC}><RegistroFeedback cliente={cliente} /></RotaProtegida>
                    } />
                    <Route path="/consulta-feedback" element={
                        <RotaProtegida permitido={podeSAC}><ConsultaFeedback cliente={cliente} /></RotaProtegida>
                    } />
                    <Route path="/gestao-usuarios" element={
                        <RotaProtegida permitido={souMaster}><GestaoUsuarios portalSession={portalSession} /></RotaProtegida>
                    } />
                    <Route path="/gestao-usuarios/novo" element={
                        <RotaProtegida permitido={souMaster}><CriarUsuarioSecundario portalSession={portalSession} onUsuarioCriado={recarregarUsuariosSecundarios} /></RotaProtegida>
                    } />
                    <Route path="/gestao-usuarios/:cdUsuario/editar" element={
                        <RotaProtegida permitido={souMaster}><EditarUsuarioSecundario portalSession={portalSession} onUsuarioAtualizado={recarregarUsuariosSecundarios} /></RotaProtegida>
                    } />
                </Routes>
                <Footer />
                <ChatWidget />
            </div>
        </Router>
    );
}

export default App;
